import React from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { BulletedList } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bulletedList/BulletedList'
import { BulletIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/BulletIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

interface SupervisorUsageGuidelinesModalProps {
  isModalOpen: boolean
  onClose: () => void
}

const Container = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const HeaderContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const BulletedListContainer = styled.View(({ theme }) => ({
  marginLeft: theme.spacing['12px'],
}))

const BulletIconContainer = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['12px'],
}))

export const SupervisorUsageGuidelinesModal: React.FC<SupervisorUsageGuidelinesModalProps> = ({
  isModalOpen,
  onClose,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const bulletPoints = [
    formatMessage(
      {
        defaultMessage:
          '<bold>How to use this data:</bold> Use these metrics as a starting point for further inquiry and reflection. These insights are intended to supplement your independent assessment of a provider’s clinical practice and can be used to inform coaching, development needs, and recognition.',
        description: 'Bullet point about how to use the ai-metrics driven data',
      },
      {
        bold: (text: string) => <BodyText size={BodyTextSize.DEFAULT} bold text={text} color={colors.textSecondary} />,
      },
    ),
    formatMessage(
      {
        defaultMessage:
          '<bold>How NOT to use this data:</bold> These metrics must never be the sole basis for any coaching, performance management or corrective actions. Decisions regarding a provider’s clinical practice or performance must be based on your independent professional review and judgment, and established performance management protocols.',
        description: 'Bullet point about how not to use the ai-metrics driven data',
      },
      {
        bold: (text: string) => <BodyText size={BodyTextSize.DEFAULT} bold text={text} color={colors.textSecondary} />,
      },
    ),
  ]

  const modalContents = (
    <Container testID={tID('SupervisorUsageGuidelinesModal-container')}>
      <HeaderContainer>
        <Title
          size={TitleSize.MEDIUM}
          text={formatMessage({
            defaultMessage: 'Supervisor usage guidelines',
            description: 'Title for the supervisor usage guidelines modal',
          })}
          testID={tID('SupervisorUsageGuidelinesModal-title')}
        />
        <PressableOpacity
          testID={tID('SupervisorUsageGuidelinesModal-close')}
          onPress={onClose}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close',
            description: 'Accessibility label for close button',
          })}
        >
          <XIcon size={24} fillColor={colors.iconDefault} />
        </PressableOpacity>
      </HeaderContainer>
      <View>
        <BodyText
          size={BodyTextSize.DEFAULT}
          text={formatMessage({
            defaultMessage:
              'These AI-driven metrics offer additional data-driven perspectives to help you support provider development. To ensure effective and fair use, please adhere to the following:',
            description: 'Introduction text for the supervisor usage guidelines modal',
          })}
          testID={tID('SupervisorUsageGuidelinesModal-intro')}
          color={colors.textSecondary}
        />
        <BulletedListContainer>
          <BulletedList
            data={bulletPoints.map((bp) => ({
              bulletIcon: (
                <BulletIconContainer>
                  <BulletIcon size={3} fillColor={colors.textSecondary} />
                </BulletIconContainer>
              ),
              value: bp,
            }))}
            size={BodyTextSize.DEFAULT}
            bulletColor={colors.textSecondary}
            spaceBetween={0}
            textColor={colors.textSecondary}
          />
        </BulletedListContainer>
      </View>
    </Container>
  )

  return (
    <Modal
      onCloseEnd={onClose}
      onRequestClose={onClose}
      disableBottomSheet
      visible={isModalOpen}
      modalContents={modalContents}
      width='550px'
      closeOnScrim
      accessibilityLabel={formatMessage({
        defaultMessage: 'Supervisor usage guidelines modal',
        description: 'Accessibility label for the supervisor usage guidelines modal',
      })}
    />
  )
}

export default SupervisorUsageGuidelinesModal
